import { Component, OnInit, Input, Output, EventEmitter, OnChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, FormBuilder, Validators, NgModel, NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import * as _ from 'lodash';

import { StudyTestTeaConfig, StudyTestTeaConfigs } from '../../../../contracts/common/study-test-tea-config.model';
import { TeaConfig } from '../../../../contracts/common/tea.model';
import { SelectedTeaConfig, TeaConfigHelperMethods } from '../../../../contracts/common/selected-tea-config.model';
import { USER_DEFINED_OPTION, USER_DEFINED_TEA, NO_TEA, NONE_OPTION } from '../../../../contracts/common/const.model';
import { Utility } from '../../../../core/helpers/utility';

@Component({
  selector: 'mc-tea',
  templateUrl: './tea.component.html',
  styleUrls: ['./tea.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TeaComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TeaComponent),
      multi: true
    }
  ]
})

export class TeaComponent implements ControlValueAccessor, Validators {
  @Input() set studyTestTeaConfigList(value: StudyTestTeaConfig[]) {
    this._studyTestTeaConfigList = value;
    this.updateStudyTestTeaConfigs(value);
  }
  get studyTestTeaConfigList(): StudyTestTeaConfig[] {
    return this._studyTestTeaConfigList;
  }

  @Input() studyTestId: number;
  @Input() unit: string;

  // For drop-down tea list.
  studyTestTeas: TeaConfig[] = [];

  // Current field value
  savedTea: SelectedTeaConfig;

  // Show or hide user-defined field region.
  isUserDefineSelected = false;
  userDefinedFreshSelection = false;

  absoluteValue: any = '';
  percentageValue: any = '';

  // List and selected list item.
  private _studyTestTeaConfigList: StudyTestTeaConfig[];
  private _chosenTeaConfig: TeaConfig;

  constructor() { }

  // BEGIN implement ControlValueAccessor

  // This is the initial value set to the component
  public writeValue(obj: any) {
    this.savedTea = obj ? obj : { teaConfig: NONE_OPTION } as SelectedTeaConfig;
    this.setSelectedTeaConfig(this.savedTea);
  }

  // Registers a callback method
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  // not used, used for touch input
  public registerOnTouched(fn: any) { }

  private propagateChange = (_: any) => { };

  // END implement ControlValueAccessor

  // BEGIN implement Validators

  public validate(formControl: FormControl) {
    // We only get errors for invalid user-defined field entries.
    if (!this.isValid()) {
      const showAbsValue = this.showAbsoluteValue;
      // Both text fields are empty
      if (this.hasEmptyUserDefinedFields(this.absoluteValue, this.percentageValue)) {
        if (showAbsValue) {
          return {
            invalidValues: {
              valid: false
            }
          };
        }

        return {
          invalidPercentageValue: {
            valid: false
          }
        };
      } else {
        // Both fields are not empty -- one or both may have invalid values entered

        // If show both fields
        if (showAbsValue) {
          // If both values are invalid
          if (!this.hasValidAbsoluteValue(this.absoluteValue) && !this.hasValidPercentageValue(this.percentageValue)) {
            return {
              invalidValues: {
                valid: false
              }
            };
          }

          if (!this.hasValidAbsoluteValue(this.absoluteValue)) {
            return {
              invalidAbsoluteValue: {
                valid: false
              }
            };
          }
        }

        if (!this.hasValidPercentageValue(this.percentageValue)) {
          return {
            invalidPercentageValue: {
              valid: false
            }
          };
        }
      }
    }

    return null;
  }

  // END implement Validators

  // Input event handlers
  public onDropDownChange(event) {
    if (event.value) {
      this.dropDownTeaConfigSelection = event.value;
      this.absoluteValue = '';
      this.percentageValue = '';
      this.updateSavedTea();
      this.propagateChange(this.savedTea);
    }
  }

  public onTextBoxChange(event) {
    this.userDefinedFreshSelection = false;
    this.updateSavedTea();
    this.propagateChange(this.savedTea);
  }

  // Selection setter and getter
  get dropDownTeaConfigSelection(): TeaConfig { return this._chosenTeaConfig; }
  set dropDownTeaConfigSelection(selectedTeaConfig: TeaConfig) {
    this._chosenTeaConfig = selectedTeaConfig;
    this.isUserDefineSelected = selectedTeaConfig && selectedTeaConfig.teaConfigId === USER_DEFINED_TEA;
    this.userDefinedFreshSelection = this.isUserDefineSelected;
    this.updateSavedTea();
  }

  get translatedGrammar(): string {
    return TeaConfigHelperMethods.GetTranslatedGrammar(this.isUserDefineSelected, this.absoluteValue, this.percentageValue, this.dropDownTeaConfigSelection, this.unit);
  }

  // Show absolute value field if there is a unit value or an existing absolute value or we have a selected analyte.
  get showAbsoluteValue(): boolean {
    return !Utility.isEmpty(this.unit) || !Utility.isEmpty(this.absoluteValue) || !Utility.isEmpty(this.studyTestId);
  }

  // Update drop-down list of tea configs and set selected value, if available.
  private updateStudyTestTeaConfigs(studyTestTeaConfigs: StudyTestTeaConfig[]) {
    this.studyTestTeas = [];
    this.studyTestTeas.push(NONE_OPTION, ...this.getTeaConfigs(studyTestTeaConfigs), USER_DEFINED_OPTION);

    if (this.savedTea && this.savedTea.studyTestId) {
      this.setSelectedTeaConfig(this.savedTea);
    }
  }

  // Set the fields with initial values.
  private setSelectedTeaConfig(selectedTeaConfig: SelectedTeaConfig) {
    if (this.isSavedTeaUserDefined()) {
      this.absoluteValue = selectedTeaConfig.userDefinedAbsoluteValue;
      this.percentageValue = selectedTeaConfig.userDefinedPercentageValue;
      this._chosenTeaConfig = this.findUseDefinedTeaOption();
      this.isUserDefineSelected = true;
      return;
    } else {
      this.isUserDefineSelected = false;
    }

    this.userDefinedFreshSelection = false;

    if (!selectedTeaConfig.teaConfig || !selectedTeaConfig.teaConfig.teaConfigId) {
      return;
    }

    // Set selected tea config from drop-down that matches the saved tea config.
    const foundTea = _.find(this.studyTestTeas,
      (tea: TeaConfig) => tea.teaConfigId === selectedTeaConfig.teaConfig.teaConfigId);

    if (foundTea) {
      this._chosenTeaConfig = foundTea;
    }
  }

  // Is it in the user-defined state?
  private isSavedTeaUserDefined() {
    if (this.savedTea.teaConfig && this.savedTea.teaConfig.teaConfigId === USER_DEFINED_TEA) {
      return true;
    }

    return Utility.isNotEmpty(this.savedTea.userDefinedAbsoluteValue) ||
      Utility.isNotEmpty(this.savedTea.userDefinedPercentageValue);
  }

  // Return user-defined item in source array.
  private findUseDefinedTeaOption() {
    return _.find(this.studyTestTeas, (tea: TeaConfig) => tea.teaConfigId === USER_DEFINED_TEA);
  }

  // The logic to persist tea config state.
  private updateSavedTea() {
    if (!this.savedTea) {
      this.savedTea = {} as SelectedTeaConfig;
    }

    // this.checkDirtyFlagForSelectedTeaConfig();

    this.savedTea.teaConfig = this.dropDownTeaConfigSelection;

    this.savedTea.studyTestId = this.studyTestId;
    if (this.isUserDefineSelected) {
      this.savedTea.userDefinedAbsoluteValue = this.absoluteValue;
      this.savedTea.userDefinedPercentageValue = this.percentageValue;
    } else {
      this.savedTea.userDefinedAbsoluteValue = '';
      this.savedTea.userDefinedPercentageValue = '';
    }
  }

  // Get unique tea configs from given StudyTestTeaConfigs
  private getTeaConfigs(studyTestTeaConfigs: StudyTestTeaConfig[]): TeaConfig[] {
    // For all analytes of instrument
    const teaConfigList: TeaConfig[] = [];
    // For each study test, collect unique tea configs
    for (const studyTestTeaConfig of studyTestTeaConfigs) {
      if (studyTestTeaConfig && studyTestTeaConfig.teaConfigs) {
      // Only add unique tea configs
      studyTestTeaConfig.teaConfigs.forEach(element => {
        if (!teaConfigList.find((value) => {
          return value.schema === element.schema;
        })) {
          teaConfigList.push(element);
        }
      });
      }
    }

    // Sort by schema name
    return teaConfigList.sort((teaConfigA, teaConfigB) => {
      return (teaConfigA.schema > teaConfigB.schema) ? 1 : -1;
    });
  }

  private isValid() {
    // Check only if user-defined option and not a fresh selection of it (to avoid showing error before input value).
    if (this.isUserDefineSelected && !this.userDefinedFreshSelection) {
      return !this.hasEmptyUserDefinedFields(this.absoluteValue, this.percentageValue)
        && this.hasValidAbsoluteValue(this.absoluteValue)
        && this.hasValidPercentageValue(this.percentageValue);
    }

    // Reset fresh selection flag.
    this.userDefinedFreshSelection = false;

    return true;
  }

  private hasEmptyUserDefinedFields(absValue, perValue) {
    return Utility.isEmpty(absValue) && Utility.isEmpty(perValue);
  }

  private hasValidAbsoluteValue(absValue) {
    return Utility.isEmpty(absValue) || Utility.isFloatGreaterThan(absValue, 0);
  }

  private hasValidPercentageValue(perValue) {
    return Utility.isEmpty(perValue) || Utility.isFloatGreatThanAndLessThan(perValue, 0, 100);
  }
}



// WEBPACK FOOTER //
// /home/runner/work/cdg-missioncontrol/cdg-missioncontrol/MissionControlClient2/MissionControl.WebUi2/mc-app/src/app/shared/ui/forms/tea/tea.component.ts